// @flow

import * as React from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, useSystem } from 'react-system';
import { getTextOfMarkdown, InlineMarked, Marked } from '../../controls/marked';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import { extendedTranslatePropertyMainType } from '../translate/propertyTypes';
import { getPlaceTitle } from '../translate/place';
import { translateOfferType } from '../translate/offerType';
import { StructuredData } from '../../controls/structured-data';
import type { AggregatesListingsFAQ_place$key } from './__generated__/AggregatesListingsFAQ_place.graphql';

import type { PlaceData } from './useAggregatesListingsPlaceData';

type Props = {|
  placeData: PlaceData,
  place: ?AggregatesListingsFAQ_place$key,
  placeMapUri: null | string,
  className?: string,
  css?: any,
|};

type Data = null | {|
  totalCount: ?number,
  olderThan3monthCount: ?number,
  olderThan3monthPercent: ?number,
  cheapest: {|
    firstPlace: string,
    firstPlaceUrl: string,
    firstPlacePrice: number,
    secondPlace: string,
    secondPlaceUrl: string,
    secondPlacePrice: number,
    thirdPlace: string,
    thirdPlaceUrl: string,
    thirdPlacePrice: number,
  |} | null,
  expensive: {|
    firstPlace: string,
    firstPlaceUrl: string,
    firstPlacePrice: number,
    secondPlace: string,
    secondPlaceUrl: string,
    secondPlacePrice: number,
    thirdPlace: string,
    thirdPlaceUrl: string,
    thirdPlacePrice: number,
  |} | null,
  popular: {|
    firstPlace: string,
    firstPlaceUrl: string,
    firstPlaceCount: number,
    secondPlace: string,
    secondPlaceUrl: string,
    secondPlaceCount: number,
    thirdPlace: string,
    thirdPlaceUrl: string,
    thirdPlaceCount: number,
  |} | null,
  pricePerM2: ?{|
    +median: ?number,
  |},
  price: ?{|
    +percentile10: ?number,
    +median: ?number,
    +percentile90: ?number,
  |},
|};

const combineData = (placeData, place, router, language): Data => {
  if (place == null) {
    return null;
  }
  const { stats, rating } = place;
  if (stats == null) {
    return null;
  }
  const forSale = placeData.offerType === 'buy';

  const totalCount = forSale ? stats.saleOffersCount : stats.rentOffersCount;
  const newerThan3monthCount = forSale
    ? stats.saleOffersCount3Month
    : stats.rentOffersCount3Month;
  const olderThan3monthCount =
    newerThan3monthCount != null && totalCount != null
      ? totalCount - newerThan3monthCount
      : null;
  const olderThan3monthPercent =
    olderThan3monthCount != null ? olderThan3monthCount / totalCount : null;

  const buildPlaceUrl = p => {
    if (p == null) {
      return '';
    }

    return router.getUrl({
      pathname: '/listings',
      query: {
        placeSlugs: JSON.stringify([
          {
            slug: p.placeForLink.slug,
            lang: language,
          },
        ]),
        offerType: JSON.stringify(placeData.offerType),
        compositePropertyType_eq:
          placeData.propertyType != null
            ? JSON.stringify(placeData.propertyType)
            : null,
      },
    }).absolute;
  };

  return {
    totalCount,
    olderThan3monthCount,
    olderThan3monthPercent,
    cheapest:
      rating != null && rating.cheapest.length === 3
        ? {
            firstPlace: rating.cheapest[0].label,
            firstPlacePrice: rating.cheapest[0].value,
            secondPlace: rating.cheapest[1].label,
            secondPlacePrice: rating.cheapest[1].value,
            thirdPlace: rating.cheapest[2].label,
            thirdPlacePrice: rating.cheapest[2].value,
            firstPlaceUrl: buildPlaceUrl(rating.cheapest[0].place),
            secondPlaceUrl: buildPlaceUrl(rating.cheapest[1].place),
            thirdPlaceUrl: buildPlaceUrl(rating.cheapest[2].place),
          }
        : null,
    expensive:
      rating != null && rating.expensive.length === 3
        ? {
            firstPlace: rating.expensive[0].label,
            firstPlacePrice: rating.expensive[0].value,
            secondPlace: rating.expensive[1].label,
            secondPlacePrice: rating.expensive[1].value,
            thirdPlace: rating.expensive[2].label,
            thirdPlacePrice: rating.expensive[2].value,
            firstPlaceUrl: buildPlaceUrl(rating.expensive[0].place),
            secondPlaceUrl: buildPlaceUrl(rating.expensive[1].place),
            thirdPlaceUrl: buildPlaceUrl(rating.expensive[2].place),
          }
        : null,
    popular:
      rating != null && rating.popular != null && rating.popular.length === 3
        ? {
            firstPlace: rating.popular[0].label,
            firstPlaceCount: rating.popular[0].value,
            secondPlace: rating.popular[1].label,
            secondPlaceCount: rating.popular[1].value,
            thirdPlace: rating.popular[2].label,
            thirdPlaceCount: rating.popular[2].value,
            firstPlaceUrl: buildPlaceUrl(rating.popular[0].place),
            secondPlaceUrl: buildPlaceUrl(rating.popular?.[1].place),
            thirdPlaceUrl: buildPlaceUrl(rating.popular?.[2].place),
          }
        : null,
    pricePerM2: forSale ? stats.salePricePerM2 : stats.yearlyRentPerM2,
    price: forSale ? stats.salePrice : stats.monthlyRent,
  };
};

const AggregatesListingsFAQInner = (props: Props) => {
  const { t, numberLocale, currency, language } = useLocale();
  const { text, maxWidths } = useTheme();
  const { media } = useSystem();
  const router = useRouter();

  const formatCurrency = n =>
    n == null
      ? t('notAvailable')
      : numberLocale.format(Math.round(n), {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        });

  const formatNumber = n =>
    n == null
      ? t('notAvailable')
      : numberLocale.format(Math.round(n), {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        });

  const place = useFragment(
    graphql`
      fragment AggregatesListingsFAQ_place on Place {
        type
        slug
        parentPlace(type: municipality) {
          placeForLink(returnSelfIfIndexIsTrue: true) {
            slug
          }
        }
        stats: listingsStatsOptional(
          propertyMainType: $propertyMainType
          numberOfRooms: $numberOfRooms
        ) {
          saleOffersCount
          rentOffersCount
          saleOffersCount3Month
          rentOffersCount3Month
          salePrice {
            percentile10
            median
            percentile90
          }
          salePricePerM2 {
            median
          }
          monthlyRent {
            percentile10
            median
            percentile90
          }
          yearlyRentPerM2 {
            median
          }
        }
        rating: municipalitiesRating(
          propertyMainType: $propertyMainType
          numberOfRooms: $numberOfRooms
          offerType: $offerType
        ) {
          cheapest {
            label
            place {
              placeForLink(returnSelfIfIndexIsTrue: false) {
                slug
              }
            }
            value
          }
          expensive {
            label
            place {
              placeForLink(returnSelfIfIndexIsTrue: false) {
                slug
              }
            }
            value
          }
          popular {
            label
            place {
              placeForLink(returnSelfIfIndexIsTrue: false) {
                slug
              }
            }
            value
          }
        }
      }
    `,
    props.place,
  );

  const { placeData } = props;
  const data = combineData(placeData, place, router, language);

  if (data == null) {
    return null;
  }

  const translatedVariables: {|
    propertyTypeSingular: string,
    propertyTypePlural: string,
    aPropertyTypeSingular: string,
    thePropertyTypeSingular: string,
    ofPropertyTypeSingular: string,
    ofPropertyTypePlural: string,
    ofThePropertyTypeSingular: string,
    ofThePropertyTypePlural: string,
    offerType: string,
    offerTypeVerb: string,
    offerTypeNoun: string,
    toOfferType: string,
    toOfferTypeNoun: string,
    forOfferType: string,
    inPlace: string,
    place: string,
    nRooms: string,
    withNRooms: string,
  |} = {
    ...extendedTranslatePropertyMainType(t, placeData.propertyType ?? ''),
    ...translateOfferType(t, placeData.offerType),
    inPlace: placeData.inLabel,
    place: placeData.label,
    nRooms:
      placeData.rooms != null
        ? t('nRooms', { count: placeData.rooms.gte })
        : '',
    withNRooms:
      placeData.rooms != null
        ? t('withNRooms', { count: placeData.rooms.gte })
        : '',
  };

  const questions = [];

  if (data.totalCount !== 0) {
    const answerData = {
      totalCount: formatNumber(data.totalCount),
      older3MonthCount: formatNumber(data.olderThan3monthCount),
      older3MonthPercent:
        data.olderThan3monthPercent == null
          ? t('notAvailable')
          : numberLocale.format(data.olderThan3monthPercent, {
              style: 'percent',
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            }),
    };
    questions.push({
      question: t('listingsFAQ_propertiesCountInLocation_question', {
        ...translatedVariables,
        count: null,
      }),
      answer: t('listingsFAQ_propertiesCountInLocation_answer', {
        ...translatedVariables,
        ...answerData,
        count: null,
      }),
    });
  }
  if (
    data.price?.percentile10 != null &&
    data.price.percentile90 != null &&
    data.price.median != null &&
    data.pricePerM2?.median != null
  ) {
    const slug =
      place?.type === 'locality'
        ? place.parentPlace?.placeForLink.slug
        : place?.slug;

    const valuationUrl =
      slug !== '' && slug != null
        ? router.getUrl({
            pathname: '/property-valuation',
            query: { slug },
          }).absolute
        : '';

    const { median, percentile10, percentile90 } = data.price;
    const medianPerM2 = data.pricePerM2.median;
    const answerData = {
      price: formatCurrency(median),
      priceFrom: formatCurrency(percentile10),
      priceTo: formatCurrency(percentile90),
      pricePerM2: formatCurrency(medianPerM2),
    };

    // In locize we use single quotes in <a> props to be valid json-ld
    questions.push({
      question: t('listingsFAQ_propertiesCostInLocation_question', {
        ...translatedVariables,
        count: null,
      }),
      answer: `${t('listingsFAQ_propertiesCostInLocation_answer', {
        ...translatedVariables,
        ...answerData,
        count: null,
      })}${
        ['state', 'municipality', 'locality'].includes(place?.type)
          ? ' ' +
            t('listingsFAQ_propertiesCostInLocation_answer_exact_price', {
              ...translatedVariables,
              ...answerData,
              inPlace: placeData.inLabel,
              url: valuationUrl,
              count: null,
            })
          : ''
      }`,
    });
  }

  if (data.cheapest != null) {
    const cheapest = data.cheapest;
    const answerData = {
      firstPlace: cheapest.firstPlace,
      firstPlaceUrl: cheapest.firstPlaceUrl,
      firstPlacePrice: formatCurrency(cheapest.firstPlacePrice),
      secondPlace: cheapest.secondPlace,
      secondPlaceUrl: cheapest.secondPlaceUrl,
      secondPlacePrice: formatCurrency(cheapest.secondPlacePrice),
      thirdPlace: cheapest.thirdPlace,
      thirdPlaceUrl: cheapest.thirdPlaceUrl,
      thirdPlacePrice: formatCurrency(cheapest.thirdPlacePrice),
    };
    if (placeData.placeType === 'municipality') {
      questions.push({
        question: t('listingsFAQ_cheapPropertiesNearMunicipality_question', {
          ...translatedVariables,
          count: null,
        }),
        answer: t('listingsFAQ_cheapPropertiesNearMunicipality_answer', {
          ...translatedVariables,
          ...answerData,
          count: null,
        }),
      });
    } else {
      questions.push({
        question: t('listingsFAQ_cheapPropertiesInLocation_question', {
          ...translatedVariables,
          count: null,
        }),
        answer: t('listingsFAQ_cheapPropertiesInLocation_answer', {
          ...translatedVariables,
          ...answerData,
          count: null,
        }),
      });
    }
  }

  if (placeData.placeType === 'municipality' && data.popular != null) {
    const popular = data.popular;
    const answerData = {
      firstPlace: popular.firstPlace,
      firstPlaceUrl: popular.firstPlaceUrl,
      firstPlaceCount: formatNumber(popular.firstPlaceCount),
      secondPlace: popular.secondPlace,
      secondPlaceUrl: popular.secondPlaceUrl,
      secondPlaceCount: formatNumber(popular.secondPlaceCount),
      thirdPlace: popular.thirdPlace,
      thirdPlaceUrl: popular.thirdPlaceUrl,
      thirdPlaceCount: formatNumber(popular.thirdPlaceCount),
    };
    questions.push({
      question: t('listingsFAQ_popularNeighbourhoodsInMunicipality_question', {
        ...translatedVariables,
        count: null,
      }),
      answer: t('listingsFAQ_popularNeighbourhoodsInMunicipality_answer', {
        ...translatedVariables,
        ...answerData,
        count: null,
      }),
    });
  }

  if (data.expensive != null) {
    const expensive = data.expensive;
    const answerData = {
      firstPlace: expensive.firstPlace,
      firstPlaceUrl: expensive.firstPlaceUrl,
      firstPlacePrice: formatCurrency(expensive.firstPlacePrice),
      secondPlace: expensive.secondPlace,
      secondPlaceUrl: expensive.secondPlaceUrl,
      secondPlacePrice: formatCurrency(expensive.secondPlacePrice),
      thirdPlace: expensive.thirdPlace,
      thirdPlaceUrl: expensive.thirdPlaceUrl,
      thirdPlacePrice: formatCurrency(expensive.thirdPlacePrice),
    };
    if (placeData.placeType === 'municipality') {
      questions.push({
        question: t('listingsFAQ_expensivePropertiesInMunicipality_question', {
          ...translatedVariables,
          count: null,
        }),
        answer: t('listingsFAQ_expensivePropertiesInMunicipality_answer', {
          ...translatedVariables,
          ...answerData,
          count: null,
        }),
      });
    } else {
      questions.push({
        question: t('listingsFAQ_expensivePropertiesInLocation_question', {
          ...translatedVariables,
          count: null,
        }),
        answer: t('listingsFAQ_expensivePropertiesInLocation_answer', {
          ...translatedVariables,
          ...answerData,
          count: null,
        }),
      });
    }
  }

  if (questions.length === 0) {
    return null;
  }

  return (
    <Box
      className={props.className}
      width={1}
      mx="auto"
      py={4}
      css={[
        media({
          maxWidth: maxWidths,
        }),
        text.body1,
      ]}
    >
      <Box
        as="h2"
        pb={3}
        css={[media([text.h5, text.h4]), { textAlign: 'center' }]}
      >
        {t('listingsFAQ_heading', {
          ...translatedVariables,
          count: null,
        })}
      </Box>
      <StructuredData
        type="Product"
        properties={{
          name: getPlaceTitle(t, placeData, 'plural'),
          offers: {
            '@type': 'AggregateOffer',
            offerCount: data.totalCount ?? 0,
            lowPrice: data.price?.percentile10 ?? 0,
            highPrice: data.price?.percentile90 ?? 0,
            priceCurrency: currency,
          },
        }}
        escapeHtml={false}
      />

      <StructuredData
        type="FAQPage"
        properties={{
          mainEntity: questions.map(({ question, answer }) => ({
            '@type': 'Question',
            name: getTextOfMarkdown(question),
            acceptedAnswer: {
              '@type': 'Answer',
              text: getTextOfMarkdown(answer),
            },
          })),
        }}
        escapeHtml={false}
      />
      {questions.map(({ question, answer }, index) => (
        <React.Fragment key={index}>
          <Box as="h3" m={0} pt={3} pb={2}>
            <InlineMarked>{question}</InlineMarked>
          </Box>
          <Marked>{answer}</Marked>
        </React.Fragment>
      ))}
    </Box>
  );
};

export const AggregatesListingsFAQ: React.AbstractComponent<Props, mixed> =
  React.memo<Props>(
    AggregatesListingsFAQInner,
    (prevProps, nextProps) =>
      prevProps.placeData.label === nextProps.placeData.label &&
      prevProps.placeData.placeType === nextProps.placeData.placeType &&
      prevProps.placeData.propertyType === nextProps.placeData.propertyType &&
      prevProps.placeData.offerType === nextProps.placeData.offerType &&
      prevProps.placeData.rooms === nextProps.placeData.rooms,
  );
